import React, { useContext, useState, useMemo } from "react";
import { toast } from "../../../utils/toast";
import { createDashboard } from "../../../service/UserDashboard";
import { getPermittedActivities } from "../../../utils/activityPermissions";
import AppContext from "../../../context/AppContext";
import CommonButton from "../../common/CommonButton";
import translations from "../../common/Translations";
import ReactSelector from "../../common/CommonReactSelector";
import { components } from "react-select";
import ModalCloseButton from "../../common/ModalCloseButton";

const Placeholder = (props) => <components.Placeholder {...props} />;

const AddDashboardModal = ({
  onClose,
  selectedOrganizationId,
  selectedWorkflowId,
  setSelectedWorkflowId,
  dashboardName,
  setDashboardName,
  selectedrole,
  setSelectedRole,
  selectedOrgId,
  data,
}) => {
  const { theme, userType, langMode } = useContext(AppContext);
  const [isLoader, setIsLoader] = useState(false);
  const id =
    userType === "super_admin" ? selectedOrgId : selectedOrganizationId;

  const roleOptions = [
    { value: "admin", label: translations[langMode].admin },
    { value: "office_user", label: translations[langMode].office_user },
  ];

  const permittedWorkflows = useMemo(() => getPermittedActivities(data), [data]);
  const workflowOptions = (permittedWorkflows || []).map((workflow) => ({
    value: String(workflow.workflowId),
    label: workflow.workflowName,
  }));
  const canSubmit =
    Boolean(dashboardName?.trim()) &&
    Boolean(selectedrole) &&
    Boolean(selectedWorkflowId);

  const getBackendMessage = (payload) =>
    payload?.body?.value ??
    payload?.body?.error ??
    payload?.header?.message ??
    payload?.message;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) {
      toast.warning(translations[langMode].pleaseFillAllFieldsBeforeSaving);
      return;
    }
    const formData = {
      dashboardName: dashboardName.trim(),
      workflowId: +selectedWorkflowId,
      role: selectedrole,
    };
    setIsLoader(true);
    createDashboard(formData, id)
      .then((response) => {
        setIsLoader(false);
        if (response.status === 200) {
          const responseData = response.data;
          if (responseData.header.code === 600) {
            toast.success(responseData.body.value);
            setDashboardName("");
            setSelectedWorkflowId("");
            setSelectedRole("");
            onClose();
          } else if (responseData.header.code === 601) {
            toast.warning(responseData.body.value);
          } else {
            const backendMessage = getBackendMessage(responseData);
            if (backendMessage) {
              toast.error(backendMessage);
            }
          }
        } else {
          const backendMessage = getBackendMessage(response?.data);
          if (backendMessage) {
            toast.error(backendMessage);
          }
        }
      })
      .catch((error) => {
        setIsLoader(false);
        console.error("Error creating dashboard:", error);
        const backendMessage =
          error?.response?.data?.body?.value ??
          error?.response?.data?.body?.error ??
          error?.response?.data?.header?.message ??
          error?.response?.data?.message ??
          error?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      });
  };

  const panelClass =
    theme === "dark"
      ? "dark-mode border border-gray-600 bg-gray-800"
      : theme === "high-contrast"
        ? "high-contrast"
        : "bg-white";

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black/50 z-50 p-4">
      <div
        className={`relative w-full max-w-2xl rounded-xl shadow-xl ${panelClass}`}
      >
        <form onSubmit={handleSubmit} className="p-6">
          <div className="mb-6 flex items-center justify-between gap-3">
            <h5 className="font-semibold text-lg">
              {translations[langMode].CreateDash}
            </h5>
            <ModalCloseButton onClick={onClose} ariaLabel="Close" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="dashboardName"
                className="text-sm font-medium text-gray-900 dark:text-white"
              >
                {translations[langMode].dashboardName}
              </label>
              <input
                id="dashboardName"
                name="dashboardName"
                value={dashboardName}
                onChange={(e) => setDashboardName(e.target.value)}
                placeholder={translations[langMode].dashboardName}
                className="w-full border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-[#236DB4]/30 focus:border-[#236DB4] block p-2.5"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label
                htmlFor="role"
                className="text-sm font-medium text-gray-900 dark:text-white"
              >
                {translations[langMode].Role}
              </label>
              <ReactSelector
                id="role"
                options={roleOptions}
                value={roleOptions.find((o) => o.value === selectedrole) || null}
                onChange={(opt) => setSelectedRole(opt?.value || "")}
                components={Placeholder}
                placeholder={`${translations[langMode].Select} ${translations[langMode].Role}`}
                height="46px"
                isClearable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
              />
            </div>

            <div className="flex flex-col gap-2 sm:col-span-2">
              <label
                htmlFor="workflowId"
                className="text-sm font-medium text-gray-900 dark:text-white"
              >
                {translations[langMode].Activity}
              </label>
              <ReactSelector
                id="workflowId"
                options={workflowOptions}
                value={
                  workflowOptions.find(
                    (o) => o.value === String(selectedWorkflowId)
                  ) || null
                }
                onChange={(opt) => setSelectedWorkflowId(opt?.value || "")}
                components={Placeholder}
                placeholder={`${translations[langMode].Select} ${translations[langMode].Activity}`}
                height="46px"
                isClearable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                maxMenuHeight="200px"
              />
            </div>
          </div>

          <div className="flex justify-end items-center mt-6 gap-3">
            <CommonButton colorIntent="danger"
              type="button"
              onClick={onClose}
              variant="danger"
              tooltipText={translations[langMode].cancelandclose}
              tooltipPosition="left"
            >
              {translations[langMode].cancel}
            </CommonButton>
            <CommonButton colorIntent="success"
              variant="primary"
              tooltipText={translations[langMode].createdashboardtooltip}
              tooltipPosition="top"
              className={
                isLoader || !canSubmit
                  ? "bg-opacity-60 cursor-not-allowed"
                  : "bg-opacity-100"
              }
              type="submit"
              disabled={isLoader || !canSubmit}
            >
              {isLoader
                ? translations[langMode].creating
                : translations[langMode].create}
            </CommonButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDashboardModal;
